import { useCallback, useEffect, useRef, useState } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Loader2, X, Sparkles, Info, ImagePlus, Download,
} from "lucide-react";
import { downloadCadKit } from "@/lib/cadExport";
import { getBackendUrl } from "@/lib/backend";
import { renderImageUrl } from "@/lib/imageUrl";
import { formatApiError } from "@/lib/apiError";
import { cadDownloadSlug } from "@/lib/cadFilename";
import HistoryTile from "@/components/cad/HistoryTile";
import GalleryPicker from "@/components/cad/GalleryPicker";
import MeshViewer3D from "@/components/cad/MeshViewer3D";
import MeshDownloadMenu from "@/components/cad/MeshDownloadMenu";

const BACKEND = getBackendUrl();
const SLOTS = [
  { key: "front", label: "Front", hint: "0° · required" },
  { key: "left",  label: "Left",  hint: "90°" },
  { key: "back",  label: "Back",  hint: "180°" },
  { key: "right", label: "Right", hint: "270°" },
];
const POLL_INTERVAL_MS = 4000;
const MAX_POLL_MIN = 5;
// Two-tier CAD pricing — mirrors the backend `tripo_3d` / `tripo_3d_hd`
// credit costs in deps.py. Keep these in sync.
const CAD_COST_STANDARD = 15;
const CAD_COST_HD = 25;

/**
 * CadDialog — single workshop for everything CAD.
 *
 * Three concerns in one dialog:
 *  1. **Pick gallery views** → submit a Tripo3D multiview→3D mesh task
 *     using the user's existing studio renders (no device upload).
 *  2. **Live 3D preview** of the current OR a selected past mesh
 *     (lazy-loaded `<model-viewer>` keeps initial bundle small).
 *  3. **Bundled CAD .zip download** — when a Front gallery render with a
 *     saved BOM is selected, the .zip kit automatically uses that render's
 *     materials AND embeds the generated .glb inside.
 *
 * Also surfaces the user's recent jobs (history list) so previous CADs
 * stay accessible — meshes are stored for 30 days in the user's Facetra
 * workspace and re-downloadable any time.
 */
export default function CadDialog({
  open,
  onOpenChange,
  displayed,
  gallery,
  onOpenMaterials,
  quota,
  onCreditsChanged,
  onRequestCadMultiview,
  onMultiviewRefine,
}) {
  const [tab, setTab] = useState("mesh"); // "mesh" | "zip"
  const [picks, setPicks] = useState({ front: null, left: null, back: null, right: null });
  const [pickerOpenFor, setPickerOpenFor] = useState(null); // "front" | "left" | ...
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [meshObjectUrl, setMeshObjectUrl] = useState(null);
  const [meshLoading, setMeshLoading] = useState(false);
  const [meshError, setMeshError] = useState(null);
  const [meshProgress, setMeshProgress] = useState(0);
  const [viewerReady, setViewerReady] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [exportingZip, setExportingZip] = useState(false);
  const [cadMvLoading, setCadMvLoading] = useState(false);
  const [formatMenuOpen, setFormatMenuOpen] = useState(false);
  // "detailed" cranks Tripo's face_limit so jewellery casting / 3D-print
  // workflows get the dense mesh they need. Standard uses Tripo defaults.
  const [convertQuality, setConvertQuality] = useState("standard");
  // 3D quality toggle. Off (default) = Standard tier · 15 credits · 4K PBR.
  //   ↳ Fast, cheap, great for design review + client presentations.
  // On                       = HD tier · 25 credits · native 8K PBR + quad mesh.
  //   ↳ Photoreal renders + subdivision-friendly quad topology for Rhino /
  //     Blender retopo work. Uses Tripo's `extreme` texture tier + quad
  //     output — see backend `hd_mode` in routers/tripo.py.
  //   Default is OFF so nobody accidentally spends 10 extra credits.
  const [hdMode, setHdMode] = useState(false);
  const pollRef = useRef(null);

  // ----- Lifecycle -----
  useEffect(() => {
    if (!open) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      setJob(null);
      setPicks({ front: null, left: null, back: null, right: null });
      setPickerOpenFor(null);
      setSubmitting(false);
      setDownloading(false);
      if (meshObjectUrl) URL.revokeObjectURL(meshObjectUrl);
      setMeshObjectUrl(null);
      setMeshLoading(false);
      setMeshError(null);
      setMeshProgress(0);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy-load <model-viewer> (~600KB with three.js) on first open.
  // We resolve `viewerReady` only after the web-component is REGISTERED
  // (`customElements.whenDefined`), not just when the JS module resolves.
  // Without that gate, the dynamic import can resolve before the
  // <model-viewer> tag actually upgrades, leaving us stuck on the loading
  // overlay forever.
  useEffect(() => {
    if (!open || viewerReady) return;
    let cancelled = false;
    (async () => {
      try {
        await import("@google/model-viewer");
        if (typeof window !== "undefined" && "customElements" in window) {
          await customElements.whenDefined("model-viewer");
        }
        if (!cancelled) setViewerReady(true);
      } catch (err) {
        console.warn("model-viewer load failed", err);
        // Still flip the flag so the UI doesn't deadlock — model-viewer's
        // own poster attribute will at minimum show the Tripo preview.
        if (!cancelled) setViewerReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open, viewerReady]);

  // Load history on open
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await axios.get(`${API}/studio/cad/tripo/jobs`);
      setHistory(r.data?.items || []);
    } catch (err) {
      console.warn("history load failed", err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);
  useEffect(() => { if (open) loadHistory(); }, [open, loadHistory]);

  // Live-poll history tiles whose status is still `running` / `queued`.
  // The user might have closed and re-opened the dialog while Tripo was
  // grinding through a job — without this, the tile sits frozen at the
  // last-cached progress until the user reopens or hits regenerate.
  // Hits the existing per-job poll endpoint (which also handles refund,
  // GridFS rehydration, preview persistence, etc.) every 5 s.
  useEffect(() => {
    if (!open) return undefined;
    const runningIds = history
      .filter((h) => ["running", "queued"].includes((h.status || "").toLowerCase()))
      .map((h) => h.job_id);
    if (runningIds.length === 0) return undefined;
    const tick = async () => {
      const results = await Promise.allSettled(
        runningIds.map((jid) =>
          axios.get(`${API}/studio/cad/tripo/jobs/${jid}`).then((r) => r.data),
        ),
      );
      setHistory((prev) =>
        prev.map((h) => {
          const fr = results.find(
            (r) => r.status === "fulfilled" && r.value?.job_id === h.job_id,
          );
          if (!fr) return h;
          const fresh = fr.value;
          return {
            ...h,
            status: fresh.status,
            progress: fresh.progress,
            mesh_ready: fresh.mesh_ready,
            mesh_size_bytes: fresh.mesh_size_bytes,
            rendered_image_url: fresh.rendered_image_url,
            error_message: fresh.error_message,
          };
        }),
      );
    };
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [open, history]);

  /** Permanently delete a CAD job (blocked while running). */
  const deleteHistoryJob = useCallback(async (h) => {
    const friendlyDate = new Date(h.created_at).toLocaleString(undefined, {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
    if (!window.confirm(
      `Delete this 3D mesh from ${friendlyDate}? This can't be undone.`,
    )) return;
    try {
      await axios.delete(`${API}/studio/cad/tripo/jobs/${h.job_id}`);
      setHistory((prev) => prev.filter((x) => x.job_id !== h.job_id));
      // If the deleted job is currently active, clear the viewer.
      setJob((curr) => (curr?.job_id === h.job_id ? null : curr));
      toast.success("Mesh deleted.");
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed."));
    }
  }, []);

  // Fetch mesh blob when the current job's mesh is ready. Uses axios so
  // the session cookie tags along consistently with the rest of the app
  // (the previous fetch() call worked in most browsers but silently
  // hung in Safari/Cloudflare-edge cases when cookies were stripped).
  const fetchMeshBlob = useCallback(async () => {
    if (!job?.job_id) return;
    setMeshError(null);
    setMeshProgress(0);
    setMeshLoading(true);
    try {
      const res = await axios.get(
        `${API}/studio/cad/tripo/jobs/${job.job_id}/download?inline=1`,
        {
          responseType: "blob",
          timeout: 90000,
          onDownloadProgress: (evt) => {
            // GLBs are typically ~4–12MB; surface percent when the
            // server announces total bytes, otherwise show downloaded MB.
            if (evt.total) {
              setMeshProgress(Math.min(100, Math.round((evt.loaded / evt.total) * 100)));
            } else if (evt.loaded) {
              setMeshProgress(-Math.round(evt.loaded / 1024 / 1024)); // negative = MB readout
            }
          },
        },
      );
      const url = URL.createObjectURL(res.data);
      setMeshObjectUrl(url);
      setMeshProgress(100);
    } catch (err) {
      const status = err?.response?.status;
      const reason =
        status === 401 ? "Session expired — sign in again to view the 3D preview."
        : status === 404 ? "Mesh file not found on the server."
        : status === 409 ? "Mesh isn't fully ready yet. Refresh in a moment."
        : err?.code === "ECONNABORTED" ? "Preview download timed out. Try the Download .glb button."
        : err?.response?.data?.detail || err?.message || "Preview download failed.";
      console.warn("mesh preview fetch failed", status, err);
      setMeshError(String(reason));
    } finally {
      setMeshLoading(false);
    }
  }, [job?.job_id]);

  useEffect(() => {
    if (!job?.mesh_ready || meshObjectUrl || meshLoading || meshError) return;
    fetchMeshBlob();
  }, [job?.mesh_ready, meshObjectUrl, meshLoading, meshError, fetchMeshBlob]);

  // ----- Derived -----
  const presentCount = Object.values(picks).filter(Boolean).length;
  const cadCost = hdMode ? CAD_COST_HD : CAD_COST_STANDARD;
  const insufficientCredits = !!quota && Number(quota.remaining ?? 0) < cadCost;
  const canGenerate = !!picks.front && presentCount >= 2 && !submitting && !job && !insufficientCredits;
  const pickFront = picks.front ? gallery?.find((g) => g.id === picks.front) : null;
  // When a Tripo job is loaded from history, prefer its front_gen_id; else the live pick.
  const zipSourceRender = (() => {
    if (job?.front_gen_id) {
      const g = gallery?.find((g) => g.id === job.front_gen_id);
      if (g) return g;
    }
    if (pickFront) return pickFront;
    return displayed;
  })();

  // ----- Helpers -----
  const assignPick = (slot, genId) => {
    setPicks((p) => {
      const next = { ...p };
      // If this gen_id is already used in another slot, free it first.
      for (const k of Object.keys(next)) if (next[k] === genId) next[k] = null;
      next[slot] = genId;
      return next;
    });
    setPickerOpenFor(null);
  };
  const clearPick = (slot) => setPicks((p) => ({ ...p, [slot]: null }));

  // The CAD-grade multiview button now ONLY runs on the CURRENTLY DISPLAYED
  // render. Previously we fell back to "first cad_ready render in gallery"
  // when displayed wasn't cad_ready — but that silently used an older,
  // unrelated bare-metal piece (e.g. when a designer refined a bare-metal
  // render but the refined child lost its render_kind tag), producing
  // views off the wrong source. Now: if the displayed render isn't a
  // bare-metal CAD-ready front, the button is hidden and the tip below
  // tells the designer to convert this render to bare-metal first.
  const cadMvSource = (() => {
    const isCadFront = (g) => g && g.render_kind === "cad_ready" && (g.view || "front") === "front";
    return isCadFront(displayed) ? displayed : null;
  })();

  // How many of L/B/R already exist as bare-metal siblings of the source?
  const cadMvExistingViews = (() => {
    if (!cadMvSource || !gallery) return new Set();
    return new Set(
      gallery
        .filter((g) => g.parent_id === cadMvSource.id && g.render_kind === "cad_ready")
        .map((g) => g.view),
    );
  })();
  const cadMvAllPresent =
    !!cadMvSource &&
    ["left", "back", "right"].every((v) => cadMvExistingViews.has(v));

  const runCadGradeMultiview = async ({ force = false } = {}) => {
    if (!cadMvSource || !onRequestCadMultiview) return;
    if (insufficientCredits) {
      toast.error("Not enough credits. Top up to continue.");
      return;
    }
    setCadMvLoading(true);
    try {
      const result = await onRequestCadMultiview(cadMvSource.id, { force });
      // Auto-fill picks: front = the cad-ready source itself, L/B/R = new entries.
      const next = { front: cadMvSource.id, left: null, back: null, right: null };
      const entries = result?.entries || [];
      for (const e of entries) {
        if (e.view && next[e.view] === null) next[e.view] = e.id;
      }
      // Also pick up any pre-existing bare-metal siblings still in gallery.
      if (gallery) {
        for (const g of gallery) {
          if (g.parent_id !== cadMvSource.id) continue;
          if (g.render_kind !== "cad_ready") continue;
          if (g.view && next[g.view] === null) next[g.view] = g.id;
        }
      }
      setPicks(next);
      toast.success("CAD-grade multiview ready · slots auto-filled.");
    } catch (err) {
      // Surface the backend-supplied `detail` (which now includes the
      // actual engine error message) instead of axios's generic
      // "Request failed with status code 502" string.
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "CAD-grade multiview failed.";
      toast.error(String(detail));
    } finally {
      setCadMvLoading(false);
    }
  };

  const submitFromGallery = async () => {
    if (!canGenerate) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/studio/cad/tripo/from-gallery`, {
        front_gen_id: picks.front,
        left_gen_id: picks.left,
        back_gen_id: picks.back,
        right_gen_id: picks.right,
        // HD tier → backend swaps in `extreme` texture + quad mesh and
        // charges `tripo_3d_hd` (25 credits) instead of `tripo_3d` (15).
        // See routers/tripo.py::tripo_submit_from_gallery.
        hd_mode: hdMode,
      });
      const j = r.data;
      setJob({ ...j, progress: 0, mesh_ready: false, front_gen_id: picks.front });
      if (j.wallet) onCreditsChanged?.(j.wallet);
      toast.success(`3D generation started · 1–3 min · ${j.credits_charged ?? cadCost} credits charged`);
      const started = Date.now();
      pollRef.current = setInterval(async () => {
        try {
          const s = await axios.get(`${API}/studio/cad/tripo/jobs/${j.job_id}`);
          setJob((cur) => ({ ...cur, ...s.data }));
          if (["success", "failed", "cancelled", "expired", "banned"].includes(s.data.status)) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            if (s.data.status === "success" && s.data.mesh_ready) {
              toast.success("3D mesh ready · preview below");
              loadHistory();
            } else if (s.data.status !== "success") {
              if (s.data.credits_refunded) {
                toast.error(`${s.data.error_message || `Generation ${s.data.status}.`} · ${s.data.credits_charged ?? cadCost} credits refunded.`);
                onCreditsChanged?.();
              } else {
                toast.error(s.data.error_message || `Generation ${s.data.status}.`);
              }
              loadHistory();
            }
          } else if (Date.now() - started > MAX_POLL_MIN * 60 * 1000) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            toast.error("Polling timed out · open History to retry.");
          }
        } catch (err) {
          console.warn("tripo poll error", err);
        }
      }, POLL_INTERVAL_MS);
    } catch (err) {
      toast.error(formatApiError(err, "Could not start generation."));
    } finally {
      setSubmitting(false);
    }
  };

  const loadHistoryJob = (h) => {
    if (!h?.mesh_ready) return;
    if (meshObjectUrl) URL.revokeObjectURL(meshObjectUrl);
    setMeshObjectUrl(null);
    setMeshLoading(false);
    // Stop any in-progress polling — we're switching context.
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setJob({
      job_id: h.job_id,
      status: h.status,
      progress: 100,
      mesh_ready: true,
      mesh_size_bytes: h.mesh_size_bytes,
      rendered_image_url: h.rendered_image_url,
      front_gen_id: h.front_gen_id,
    });
  };

  const downloadMeshFormat = async (fmt) => {
    if (!job?.mesh_ready) return;
    if (fmt === "GLB") return downloadMesh();
    if (insufficientCredits) {
      toast.error("Not enough credits — format conversion costs 1 credit.");
      return;
    }
    setDownloading(true);
    const toastId = toast.loading(`Converting to ${fmt}… ~10 s`);
    try {
      const res = await axios.get(
        `${API}/studio/cad/tripo/jobs/${job.job_id}/download/${fmt}`,
        {
          responseType: "blob",
          timeout: 180000,
          params: { quality: convertQuality },
        },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facetra_3d_${cadDownloadSlug(job.job_id)}.${fmt.toLowerCase()}`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch { /* noop */ }
        URL.revokeObjectURL(url);
      }, 1500);
      toast.success(`${fmt} downloaded · ${(res.data.size / 1024).toFixed(1)} KB · 1 credit`, { id: toastId });
      onCreditsChanged?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || `Could not convert to ${fmt}.`;
      toast.error(String(detail), { id: toastId });
    } finally {
      setDownloading(false);
      setFormatMenuOpen(false);
    }
  };

  const downloadMesh = async () => {
    if (!job?.mesh_ready) return;
    setDownloading(true);
    try {
      let blob;
      if (meshObjectUrl) {
        const r = await fetch(meshObjectUrl);
        blob = await r.blob();
      } else {
        const res = await fetch(`${API}/studio/cad/tripo/jobs/${job.job_id}/download`, { credentials: "include" });
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        blob = await res.blob();
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facetra_3d_${cadDownloadSlug(job.job_id)}.glb`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch { /* noop */ }
        URL.revokeObjectURL(url);
      }, 1500);
      toast.success(`Mesh downloaded · ${(blob.size / 1024).toFixed(1)} KB`);
    } catch (err) {
      toast.error(formatApiError(err, "Could not download the mesh."));
    } finally {
      setDownloading(false);
    }
  };

  const downloadZip = async () => {
    if (!zipSourceRender) {
      toast.error("Pick a Front view (with a saved BOM) first.");
      return;
    }
    setExportingZip(true);
    try {
      const tripoJobId = job?.mesh_ready ? job.job_id : null;
      await downloadCadKit(
        zipSourceRender,
        () => { onOpenChange(false); onOpenMaterials?.(zipSourceRender); },
        tripoJobId,
      );
    } finally {
      setExportingZip(false);
    }
  };

  // ----- Render helpers -----
  const renderSlot = (s) => {
    const genId = picks[s.key];
    const g = genId ? gallery?.find((g) => g.id === genId) : null;
    return g ? (
      <div data-testid={`cad-slot-${s.key}`} className="relative rounded-lg border border-[#D4AF37]/35 overflow-hidden bg-[#141414] group">
        <img src={renderImageUrl(BACKEND, g)} alt={s.label} className="w-full h-28 object-cover" />
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent px-2 py-1.5">
          <div className="text-[10px] font-medium text-[#D4AF37] uppercase tracking-[0.18em] leading-tight">{s.label}</div>
        </div>
        {!job && (
          <>
            <button
              type="button"
              onClick={() => clearPick(s.key)}
              data-testid={`cad-slot-${s.key}-clear`}
              className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 border border-white/15 text-white/70 hover:text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
            >
              <X className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => setPickerOpenFor(s.key)}
              className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-black/70 border border-white/15 text-white/80 text-[9px] uppercase tracking-[0.18em] opacity-0 group-hover:opacity-100 focus:opacity-100 transition"
            >
              Change
            </button>
          </>
        )}
      </div>
    ) : (
      <button
        type="button"
        disabled={!!job}
        onClick={() => setPickerOpenFor(s.key)}
        data-testid={`cad-slot-${s.key}`}
        className="w-full h-28 rounded-lg border border-dashed border-white/12 bg-[#0E0E0E] hover:border-[#D4AF37]/40 hover:bg-[#141414] transition flex flex-col items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <ImagePlus className="w-3.5 h-3.5 text-white/55" />
        <div className="text-[11px] text-white/75 font-medium leading-none">
          {s.label}{s.key === "front" && <span className="text-[#D4AF37] ml-0.5">*</span>}
        </div>
        <div className="text-[9px] uppercase tracking-[0.18em] text-white/35 leading-none">{s.hint}</div>
      </button>
    );
  };

  const statusLabel = job?.status === "queued" ? "Queued"
    : job?.status === "running" ? `Generating · ${job?.progress || 0}%`
    : job?.status === "success" && job?.mesh_ready ? "Mesh ready"
    : job?.status === "success" ? "Downloading mesh…"
    : job?.status === "failed" ? "Failed"
    : "Working";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="cad-dialog"
        className="bg-[#0B0B0B] border border-[#D4AF37]/20 text-white max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white tracking-tight">CAD workshop</DialogTitle>
          <DialogDescription className="text-xs text-white/55 leading-relaxed">
            Pick multiview renders from your gallery to generate a 3D mesh, preview it live, and download the bundled CAD archive.
          </DialogDescription>
        </DialogHeader>

        {/* Tab toggle hidden — CAD .zip kit retired; the 3D Mesh tab now
            offers multi-format download (GLB / STL / OBJ / FBX / USDZ /
            GLTF). The .zip-kit code stays in the tree as a fallback path
            but is not user-reachable. */}

        {tab === "mesh" ? (
          <div className="mt-5 space-y-5">
            <div className="rounded-xl border border-[#D4AF37]/20 bg-[#1A1408]/60 p-3.5 text-[11px] text-white/65 leading-relaxed space-y-2">
              <div className="inline-flex items-start gap-2">
                <Info className="w-3.5 h-3.5 mt-0.5 text-[#D4AF37]/80 shrink-0" />
                <span><strong className="text-[#D4AF37]">For a faithful 3D reconstruction give the engine all 4 views.</strong> Works for every piece type — rings, earrings, pendants, bracelets, bangles, necklaces, brooches, nose-pins.</span>
              </div>
              <ul className="ml-5 list-disc space-y-0.5 text-white/55 text-[10.5px]">
                <li><span className="text-white/75">Front (0°)</span> — what the wearer sees.</li>
                <li><span className="text-white/75">Left (90°)</span> — side profile, camera rotated 90° clockwise.</li>
                <li><span className="text-white/75">Back (180°)</span> — the reverse / underside. For rings: bottom of the shank. For pendants / earrings: the bail or post side. For necklaces / bangles / bracelets: the clasp / hinge side.</li>
                <li><span className="text-white/75">Right (270°)</span> — opposite side profile.</li>
              </ul>
              {cadMvSource && !cadMvAllPresent && (
                <div className="mt-2 ml-5 rounded-lg border border-[#D4AF37]/25 bg-[#D4AF37]/[0.06] p-2.5">
                  <div className="text-[10.5px] text-white/70 leading-relaxed">
                    <strong className="text-[#D4AF37]">CAD-grade multiview</strong> — generate Left · Back · Right of your bare-metal{" "}
                    <span className="text-white/85">current render</span>{" "}
                    in one engine call. Geometrically tight, perfect for mesh generation.
                  </div>
                  <button
                    type="button"
                    onClick={runCadGradeMultiview}
                    disabled={cadMvLoading || insufficientCredits || !!job}
                    data-testid="cad-grade-multiview-btn"
                    className="mt-2 inline-flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/45 text-[#D4AF37] hover:bg-[#D4AF37]/25 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {cadMvLoading ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> Generating Left · Back · Right…</>
                    ) : (
                      <><Sparkles className="w-3 h-3" /> Generate · 3 credits</>
                    )}
                  </button>
                </div>
              )}
              {cadMvSource && cadMvAllPresent && (
                <div className="mt-2 ml-5 rounded-lg border border-white/8 bg-[#0F0F0F] p-2.5 flex items-center justify-between gap-2">
                  <p className="text-white/55 text-[10.5px] leading-relaxed flex-1">
                    All four bare-metal views ready · pick them in the slots below or regenerate a fresh iteration.
                  </p>
                  <button
                    type="button"
                    onClick={() => runCadGradeMultiview({ force: true })}
                    disabled={cadMvLoading || insufficientCredits || !!job}
                    data-testid="cad-grade-multiview-regen-btn"
                    title="Discard the existing Left / Back / Right bare-metal views and regenerate them (3 credits)."
                    className="inline-flex items-center gap-1.5 text-[10.5px] px-2.5 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/20 transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {cadMvLoading ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> Regenerating…</>
                    ) : (
                      <><Sparkles className="w-3 h-3" /> Regenerate · 3</>
                    )}
                  </button>
                </div>
              )}
              {!cadMvSource && (
                <p className="ml-5 text-white/45 text-[10px]">
                  Tip: in the studio click <span className="text-[#D4AF37]">CAD ready</span> on a front render first to unlock the CAD-grade multiview generator here.
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {SLOTS.map((s) => <div key={s.key}>{renderSlot(s)}</div>)}
            </div>

            {!job ? (
              <>
                {/* HD tier toggle — Standard (15 cr, 4K PBR) vs HD (25 cr, 8K PBR + quad mesh) */}
                <div
                  className="rounded-xl border border-white/8 bg-[#0F0F0F] p-3"
                  data-testid="cad-hd-toggle-card"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-0.5">
                        Output tier
                      </div>
                      <div className="text-[13px] font-medium text-white flex items-center gap-2 flex-wrap">
                        {hdMode ? "HD · 8K PBR" : "Standard · 4K PBR"}
                        <span className="text-[#D4AF37] text-[11px] font-normal">
                          {hdMode ? `${CAD_COST_HD} cr` : `${CAD_COST_STANDARD} cr`}
                        </span>
                      </div>
                      <div className="text-white/50 text-[10.5px] mt-1 leading-relaxed">
                        {hdMode
                          ? "Native 8K PBR textures — photoreal presentation renders and hero-quality material detail. Same GLB mesh, richer surfaces."
                          : "4K PBR textures, standard mesh. Fast, great for design review and client presentations."}
                      </div>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={hdMode}
                      onClick={() => setHdMode((v) => !v)}
                      data-testid="cad-hd-toggle"
                      disabled={submitting}
                      title={
                        hdMode
                          ? `Switch to Standard · ${CAD_COST_STANDARD} credits`
                          : `Switch to HD · ${CAD_COST_HD} credits`
                      }
                      className={
                        "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition disabled:opacity-40 disabled:cursor-not-allowed " +
                        (hdMode
                          ? "bg-[#D4AF37] shadow-[0_0_18px_-4px_rgba(212,175,55,0.7)]"
                          : "bg-white/15 hover:bg-white/25")
                      }
                    >
                      <span
                        className={
                          "inline-block h-4 w-4 transform rounded-full bg-black transition " +
                          (hdMode ? "translate-x-6" : "translate-x-1")
                        }
                      />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-white/55">
                    Cost: <span className="text-[#D4AF37] font-medium" data-testid="cad-cost-preview">{cadCost} credits</span>
                    {quota?.remaining != null && (
                      <span className="text-white/35"> · balance {quota.remaining}</span>
                    )}
                  </span>
                  {insufficientCredits && <span className="text-red-300">Top up to continue.</span>}
                </div>
                {presentCount > 0 && presentCount < 4 && (
                  <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 p-2.5 text-[10.5px] text-amber-200/85 leading-relaxed">
                    <strong className="text-amber-200">{presentCount} view{presentCount > 1 ? "s" : ""} picked.</strong>
                    {" "}For closed-loop pieces (rings, bangles, bracelets, necklaces), the <span className="text-[#D4AF37]">Back (180°)</span> view is essential — it lets the engine close the loop accurately. Add the missing views from your gallery for the best result.
                  </div>
                )}
                <button
                  onClick={submitFromGallery}
                  disabled={!canGenerate}
                  data-testid="cad-generate-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3.5 hover:bg-[#F3CE5A] transition disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Generate 3D Mesh · {cadCost} credits</>}
                </button>
              </>
            ) : (
              <div data-testid="cad-job-card" className="rounded-2xl border border-[#D4AF37]/25 bg-[#141414] p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">Facetra CAD · multiview → 3D</div>
                    <div className="text-sm text-white" data-testid="cad-job-status">{statusLabel}</div>
                  </div>
                  {job?.status !== "success" && !job?.mesh_ready && (
                    <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin" />
                  )}
                </div>
                {job?.status === "running" && (
                  <div className="mt-3 h-1.5 rounded-full bg-white/8 overflow-hidden">
                    <div className="h-full bg-[#D4AF37] transition-all" style={{ width: `${Math.max(5, Math.min(100, job.progress || 0))}%` }} />
                  </div>
                )}
                {job?.error_message && (
                  <p className="mt-3 text-xs text-red-300 leading-relaxed">{job.error_message}</p>
                )}
                {job?.credits_refunded && (
                  <p className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-300/85">
                    {job?.credits_charged ?? cadCost} credits refunded to your wallet
                  </p>
                )}
                {job?.mesh_ready && (
                  <>
                    <MeshViewer3D
                      job={job}
                      meshObjectUrl={meshObjectUrl}
                      viewerReady={viewerReady}
                      meshLoading={meshLoading}
                      meshError={meshError}
                      meshProgress={meshProgress}
                      onRetry={fetchMeshBlob}
                    />
                    <MeshDownloadMenu
                      job={job}
                      downloading={downloading}
                      menuOpen={formatMenuOpen}
                      onToggleMenu={() => setFormatMenuOpen((v) => !v)}
                      convertQuality={convertQuality}
                      onChangeQuality={setConvertQuality}
                      onDownloadFormat={downloadMeshFormat}
                      insufficientCredits={insufficientCredits}
                    />
                    <p className="mt-2 text-[10px] text-white/40 text-center leading-relaxed">
                      Saved to your Facetra workspace for 30 days · automatically attached to your Studio account · downloadable anytime
                    </p>
                  </>
                )}
                {(job?.status === "failed" || job?.status === "cancelled" || job?.status === "expired" || job?.status === "banned") && (
                  <button
                    onClick={() => { setJob(null); setMeshObjectUrl(null); }}
                    className="mt-4 text-[11px] text-white/60 hover:text-white"
                  >
                    ← Start a new generation
                  </button>
                )}
              </div>
            )}

            {/* History */}
            <div className="pt-4 border-t border-white/8">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Previous 3D meshes</div>
                {loadingHistory && <Loader2 className="w-3.5 h-3.5 text-[#D4AF37]/70 animate-spin" />}
              </div>
              {history.length === 0 && !loadingHistory ? (
                <p className="text-xs text-white/40">No previous meshes yet — generate your first one above.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5" data-testid="cad-history-grid">
                  {history.map((h) => (
                    <HistoryTile
                      key={h.job_id}
                      job={h}
                      isActive={job?.job_id === h.job_id}
                      onClick={() => loadHistoryJob(h)}
                      onDelete={() => deleteHistoryJob(h)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-5 space-y-5">
            <div className="text-[11px] text-white/55 inline-flex items-start gap-2 leading-relaxed">
              <Info className="w-3.5 h-3.5 mt-0.5 text-[#D4AF37]/80 shrink-0" />
              <span>
                Layered .3dm with one Rhino layer per material in your BOM + per-layer STLs.
                Uses the Front gallery render you picked above (or the currently displayed render). When a 3D mesh exists in this session it&apos;s bundled inside automatically.
              </span>
            </div>

            {zipSourceRender ? (
              <div className="rounded-xl border border-white/10 bg-[#0F0F0F] p-3 flex items-center gap-3">
                <img src={renderImageUrl(BACKEND, zipSourceRender)} alt="Source" className="w-14 h-14 rounded object-cover border border-white/10" />
                <div className="flex-1 text-xs">
                  <div className="text-white/85">Using: <span className="text-[#D4AF37]">{zipSourceRender.style || "Custom"}{zipSourceRender.view && zipSourceRender.view !== "front" ? ` · ${zipSourceRender.view}` : ""}</span></div>
                  <div className="text-white/40 text-[10px] mt-0.5">
                    BOM: {zipSourceRender.materials ? <span className="text-emerald-300">saved</span> : <span className="text-amber-300">missing</span>}
                  </div>
                </div>
              </div>
            ) : null}

            {!zipSourceRender ? (
              <p className="text-sm text-white/55 text-center py-6">Pick a Front view in the 3D Mesh tab first (or open this dialog with a render selected).</p>
            ) : !zipSourceRender.materials ? (
              <div className="rounded-2xl border border-amber-500/25 bg-amber-500/5 p-5 text-center">
                <p className="text-sm text-white/80">Save a Bill of Materials for this render — the CAD layers come from your BOM.</p>
                <button
                  onClick={() => { onOpenChange(false); onOpenMaterials?.(zipSourceRender); }}
                  data-testid="cad-open-materials-btn"
                  className="mt-3 text-xs text-[#D4AF37] hover:text-[#F3CE5A]"
                >
                  Open Materials drawer →
                </button>
              </div>
            ) : (
              <>
                {job?.mesh_ready && (
                  <div className="rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/5 px-4 py-3 text-[11px] text-[#D4AF37]/90 inline-flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" />
                    Your generated 3D mesh will be bundled inside (mesh/model.glb).
                  </div>
                )}
                <button
                  onClick={downloadZip}
                  disabled={exportingZip}
                  data-testid="cad-zip-download-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium py-3.5 hover:bg-[#F3CE5A] transition disabled:opacity-60 shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)]"
                >
                  {exportingZip ? <><Loader2 className="w-4 h-4 animate-spin" /> Packing…</> : <><Download className="w-4 h-4" /> Download CAD .zip{job?.mesh_ready ? " + 3D mesh" : ""}</>}
                </button>
              </>
            )}
          </div>
        )}

        {/* Gallery picker overlay */}
        {pickerOpenFor && (
          <GalleryPicker
            slot={pickerOpenFor}
            gallery={gallery}
            picks={picks}
            onClose={() => setPickerOpenFor(null)}
            onPick={(g) => assignPick(pickerOpenFor, g.id)}
            onRefine={onMultiviewRefine ? (g) => { setPickerOpenFor(null); onMultiviewRefine(g); } : null}
            backendUrl={BACKEND}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

